import type { Provider } from '@angular/core';
import type { IReportRepository, IReportingRepository, ICryptoAdapter } from '@app/domain/ports';
import {
  LoadReportsUseCase,
  GenerateReportUseCase,
  GetDecryptedReportUseCase,
  DeleteReportUseCase,
  DeleteAllReportsUseCase,
} from '@app/domain/use-cases';
import { REPORT_REPOSITORY, REPORTING_REPOSITORY, CRYPTO_ADAPTER } from '../../tokens/infrastructure.tokens';
import { DekService } from '@app/domain/services';
import { AppStore } from '../../states';

/**
 * Provides Report domain use cases.
 *
 * Includes:
 * - LoadReportsUseCase
 * - GenerateReportUseCase (with encryption support)
 * - GetDecryptedReportUseCase
 * - DeleteReportUseCase
 * - DeleteAllReportsUseCase
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideReportDomain(): Provider[] {
  return [
    {
      provide: LoadReportsUseCase,
      useFactory: (repository: IReportRepository) => new LoadReportsUseCase(repository),
      deps: [REPORT_REPOSITORY],
    },
    {
      provide: GenerateReportUseCase,
      useFactory: (
        reportingRepository: IReportingRepository,
        reportRepository: IReportRepository,
        cryptoAdapter: ICryptoAdapter,
        dekService: DekService,
        appStore: InstanceType<typeof AppStore>,
      ) =>
        new GenerateReportUseCase(
          reportingRepository,
          reportRepository,
          cryptoAdapter,
          () =>
            new Promise<CryptoKey | null>((resolve, reject) =>
              dekService
                .getDekContainer()
                .execute((dek) => {
                  resolve(dek);
                  return Promise.resolve();
                })
                .catch(reject),
            ),
          () => appStore.encryptData(),
        ),
      deps: [REPORTING_REPOSITORY, REPORT_REPOSITORY, CRYPTO_ADAPTER, DekService, AppStore],
    },
    {
      provide: GetDecryptedReportUseCase,
      useFactory: (cryptoAdapter: ICryptoAdapter, dekService: DekService) =>
        new GetDecryptedReportUseCase(
          cryptoAdapter,
          () =>
            new Promise<CryptoKey | null>((resolve, reject) =>
              dekService
                .getDekContainer()
                .execute((dek) => {
                  resolve(dek);
                  return Promise.resolve();
                })
                .catch(reject),
            ),
        ),
      deps: [CRYPTO_ADAPTER, DekService],
    },
    {
      provide: DeleteReportUseCase,
      useFactory: (repository: IReportRepository) => new DeleteReportUseCase(repository),
      deps: [REPORT_REPOSITORY],
    },
    {
      provide: DeleteAllReportsUseCase,
      useFactory: (repository: IReportRepository) => new DeleteAllReportsUseCase(repository),
      deps: [REPORT_REPOSITORY],
    },
  ];
}
